import {
  Body1,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  ProgressBar,
  Spinner,
  Text,
} from '@fluentui/react-components';
import {
  bundleIcon,
  Folder16Filled,
  Folder16Regular,
} from '@fluentui/react-icons';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useDataHubClient } from '../../../context/DataHubClientContext.js';
import { DataHubService } from '../../../services/dataHubService.js';
import { useScenarioFactory } from '../../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../../telemetry/scenarioError.js';
import { ScenarioName } from '../../../telemetry/scenarios.js';

import type {
  DatasetUploadProgress,
  DatasetUploadResult,
} from './datasetUploadUtils.js';
import {
  extractMetadata,
  parseFolderFiles,
  uploadDatasetFolder,
} from './datasetUploadUtils.js';
import type {
  ParsedFolderEntry,
  UploadFolderDialogProps,
  UploadStage,
} from './UploadFolderDialog.interface.js';
import { useStyles } from './UploadFolderDialog.style.js';

const FolderIcon = bundleIcon(Folder16Filled, Folder16Regular) as React.FC;

const BYTES_PER_KB = 1024;

const STAGE_LABELS: Record<UploadStage, string> = {
  preview: 'Ready to upload',
  'creating-entries': 'Creating data entries...',
  uploading: 'Uploading files...',
  done: 'Upload complete',
  error: 'Upload failed',
};

function formatBytes(bytes: number): string {
  if (bytes < BYTES_PER_KB) return `${bytes} B`;
  if (bytes < BYTES_PER_KB * BYTES_PER_KB)
    return `${(bytes / BYTES_PER_KB).toFixed(1)} KB`;
  return `${(bytes / (BYTES_PER_KB * BYTES_PER_KB)).toFixed(1)} MB`;
}

export function UploadFolderDialog({
  open,
  dataSetId,
  defaultDataType,
  onClose,
  onComplete,
  onStatusChanged,
}: UploadFolderDialogProps) {
  const styles = useStyles();
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const scenarioFactory = useScenarioFactory();
  const abortRef = useRef<AbortController | null>(null);

  const [folderEntries, setFolderEntries] = useState<ParsedFolderEntry[]>([]);
  const [stage, setStage] = useState<UploadStage>('preview');
  const [progress, setProgress] = useState<DatasetUploadProgress | null>(null);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [result, setResult] = useState<DatasetUploadResult | null>(null);

  const totalFiles = folderEntries.reduce((sum, e) => sum + e.files.length, 0);
  const totalSize = folderEntries.reduce(
    (sum, e) => sum + e.files.reduce((s, f) => s + f.size, 0),
    0,
  );

  const handleSelectFolder = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.setAttribute('webkitdirectory', '');
    input.onchange = () => {
      if (input.files !== null && input.files.length > 0) {
        const parsed = parseFolderFiles(input.files);
        void extractMetadata(parsed)
          .then((enriched) => {
            setFolderEntries(enriched);
          })
          .catch(() => {
            setFolderEntries(parsed);
          })
          .finally(() => {
            setStage('preview');
            setErrorMessages([]);
            setResult(null);
            setProgress(null);
          });
      }
    };
    input.click();
  }, []);

  const handleUpload = useCallback(async () => {
    if (folderEntries.length === 0) return;

    const abort = new AbortController();
    abortRef.current = abort;
    setErrorMessages([]);
    setResult(null);

    // Open one operational scenario for the whole folder-upload flow. The util
    // marks each milestone and carries the scenario's correlation GUID to the
    // single summary TEDH call; this component completes it exactly once.
    const scenario = scenarioFactory.newScenario(
      ScenarioName.DatalakeUploadFolder,
      { abort },
    );

    try {
      const uploadResult = await uploadDatasetFolder(
        service,
        dataSetId,
        defaultDataType,
        folderEntries,
        (p) => {
          if (abort.signal.aborted) return;
          setProgress(p);
          setStage(p.phase === 'done' ? 'done' : p.phase);
        },
        abort.signal,
        scenario,
      );

      if (abort.signal.aborted) {
        scenario.complete('abandoned', {
          'Usage.ErrorCategory': 'cancelled',
        });
        return;
      }

      setResult(uploadResult);
      if (uploadResult.errors.length > 0) {
        setErrorMessages(uploadResult.errors);
      }
      setStage(uploadResult.success ? 'done' : 'error');
      scenario.complete(uploadResult.success ? 'success' : 'failure', {
        uploaded_file_count: uploadResult.uploadedFileCount,
        created_entry_count: uploadResult.createdEntryCount,
        error_count: uploadResult.errors.length,
      });
      if (uploadResult.createdEntryCount > 0) {
        onStatusChanged?.();
      }
    } catch (err) {
      if (!abort.signal.aborted) {
        const classification = classifyScenarioError(err);
        scenario.complete(classification.status, classification.data);
        setErrorMessages([
          err instanceof Error ? err.message : 'Upload failed unexpectedly.',
        ]);
        setStage('error');
      } else {
        scenario.complete('abandoned', {
          'Usage.ErrorCategory': 'cancelled',
        });
      }
    }
  }, [
    folderEntries,
    service,
    dataSetId,
    defaultDataType,
    onStatusChanged,
    scenarioFactory,
  ]);

  const handleClose = useCallback(() => {
    abortRef.current?.abort();
    setFolderEntries([]);
    setStage('preview');
    setErrorMessages([]);
    setResult(null);
    setProgress(null);
    if (stage === 'done' || stage === 'error') {
      onComplete();
    }
    onClose();
  }, [onClose, onComplete, stage]);

  const isUploading = stage === 'creating-entries' || stage === 'uploading';

  return (
    <Dialog
      open={open}
      onOpenChange={(_, data) => {
        if (!data.open && !isUploading) {
          handleClose();
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Upload from Folder</DialogTitle>
          <DialogContent className={styles.dialogContent}>
            {stage === 'preview' && folderEntries.length === 0 && (
              <Body1>
                Select a folder to upload. Each subfolder will become a data
                entry, and files within each subfolder will be attached to that
                entry.
              </Body1>
            )}

            {stage === 'preview' && folderEntries.length > 0 && (
              <>
                <div className={styles.summary}>
                  <Text>{folderEntries.length} entries</Text>
                  <Text>{totalFiles} files</Text>
                  <Text>{formatBytes(totalSize)}</Text>
                </div>
                <div className={styles.entryList}>
                  {folderEntries.map((entry) => (
                    <div key={entry.name} className={styles.entryItem}>
                      <Text className={styles.entryName}>
                        <FolderIcon /> {entry.name}
                      </Text>
                      <Text className={styles.fileCount}>
                        {entry.files.length} file
                        {entry.files.length !== 1 ? 's' : ''}
                      </Text>
                    </div>
                  ))}
                </div>
              </>
            )}

            {isUploading && (
              <div className={styles.progressSection}>
                <Body1 className={styles.progressLabel}>
                  {STAGE_LABELS[stage]}
                </Body1>
                <ProgressBar
                  value={
                    progress !== null && progress.totalFiles > 0
                      ? progress.completedFiles / progress.totalFiles
                      : undefined
                  }
                />
                {progress !== null && progress.totalFiles > 0 && (
                  <Body1 className={styles.progressLabel}>
                    Uploaded {progress.completedFiles} of {progress.totalFiles}{' '}
                    files
                    {progress.failedFiles > 0 &&
                      ` (${progress.failedFiles} failed)`}
                    {progress.currentEntry !== '' &&
                      ` — ${progress.currentEntry}`}
                  </Body1>
                )}
              </div>
            )}

            {(stage === 'done' || stage === 'error') && result !== null && (
              <div className={styles.resultSummary}>
                {result.uploadedFileCount > 0 && (
                  <Body1 className={styles.successText}>
                    {result.uploadedFileCount} file
                    {result.uploadedFileCount !== 1 ? 's' : ''} uploaded
                    successfully across {result.createdEntryCount} entr
                    {result.createdEntryCount !== 1 ? 'ies' : 'y'}.
                  </Body1>
                )}
                {errorMessages.length > 0 && (
                  <div className={styles.errorList}>
                    {errorMessages.map((msg) => (
                      <Body1 key={msg} className={styles.errorText}>
                        {msg}
                      </Body1>
                    ))}
                  </div>
                )}
              </div>
            )}

            {stage === 'error' &&
              result === null &&
              errorMessages.length > 0 && (
                <Body1 className={styles.errorText}>{errorMessages[0]}</Body1>
              )}
          </DialogContent>
          <DialogActions>
            {stage === 'preview' && (
              <>
                <Button
                  appearance="secondary"
                  icon={<FolderIcon />}
                  onClick={handleSelectFolder}
                >
                  {folderEntries.length > 0 ? 'Change Folder' : 'Select Folder'}
                </Button>
                <Button
                  appearance="primary"
                  disabled={folderEntries.length === 0}
                  onClick={() => {
                    void handleUpload();
                  }}
                >
                  Upload
                </Button>
              </>
            )}
            {isUploading && (
              <Button appearance="secondary" disabled>
                <Spinner size="tiny" /> Uploading...
              </Button>
            )}
            {(stage === 'done' || stage === 'error') && (
              <Button appearance="primary" onClick={handleClose}>
                Close
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
